import { type Channel, ProviderChannel } from './Channel'
import { type IProviderService } from './provider/IProviderService'
import { getInstance, requireInstance, tabUuid } from './realtimeInstance'

const channels: Map<string, Channel> = new Map()
const namespaces: Map<string, string | undefined> = new Map()

function createChannel(name: string, service: IProviderService, namespace?: string): Channel {
  service.subscribe(name, namespace)
  const channel = new ProviderChannel(name, service, (unsubscribed: string) => {
    channels.delete(unsubscribed)
    namespaces.delete(unsubscribed)
  })
  channels.set(name, channel)
  namespaces.set(name, namespace)

  return channel
}

function assertSameNamespace(name: string, namespace?: string): void {
  const known = namespaces.get(name)

  if (known === namespace) {
    return
  }

  throw new Error(
    `Channel "${name}" is already subscribed in namespace "${known ?? "the app's project"}", ` +
      `it cannot also be subscribed in "${namespace ?? "the app's project"}".`,
  )
}

export function usePrevioRealtime() {
  function subscribe(name: string, namespace?: string): Channel {
    const service = requireInstance()
    const cached = channels.get(name)

    if (cached) {
      assertSameNamespace(name, namespace)
      service.connect()

      return cached
    }

    const channel = createChannel(name, service, namespace)
    service.connect()

    return channel
  }

  function setKey(keyBase64: string | null): void {
    requireInstance().setKey(keyBase64)
  }

  function disconnect(): void {
    getInstance()?.disconnect()
    channels.clear()
    namespaces.clear()
  }

  return {
    setKey,
    subscribe,
    disconnect,
    tabUuid,
  }
}
